import { HeroSection } from '@/components/sections/HeroSection';
import { MarqueeSection } from '@/components/sections/MarqueeSection';
import { TrustBar } from '@/components/sections/TrustBar';
import { FreeSecurityChecks } from '@/components/sections/FreeSecurityChecks';
import { AcodiaWatchSection } from '@/components/sections/AcodiaWatchSection';
import { ServicesSection } from '@/components/sections/ServicesSection';
import { TerminalSection } from '@/components/sections/TerminalSection';
import { CoursesSection } from '@/components/sections/CoursesSection';
import { WhySection } from '@/components/sections/WhySection';
import { ProcessSection } from '@/components/sections/ProcessSection';
import { CTABand } from '@/components/sections/CTABand';
import { FAQSection, faqs } from '@/components/sections/FAQSection';
import { ContactSection } from '@/components/sections/ContactSection';
import { Footer } from '@/components/sections/Footer';
import { Navbar } from '@/components/layout/Navbar';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Acodia',
  url: 'https://acodia.co.uk',
  logo: 'https://acodia.co.uk/images/logo/logo-nav-64.png',
  description: 'London-based specialist practice offering cybersecurity monitoring, tech training, career development and digital marketing.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'London',
    addressCountry: 'GB',
    postalCode: 'N1 7GU',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'connect@acodia.co.uk',
    contactType: 'customer service',
  },
  sameAs: [
    'https://linkedin.com/company/acodia',
    'https://twitter.com/acodia',
    'https://github.com/Dev-Acodia',
  ],
};

const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: 'Acodia',
  url: 'https://acodia.co.uk',
  telephone: '0330 122 1202',
  email: 'connect@acodia.co.uk',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'London',
    addressCountry: 'GB',
    postalCode: 'N1 7GU',
  },
  openingHours: 'Mo-Fr 09:00-18:00',
  priceRange: '££',
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

export default function HomePage() {
  useSEO({
    title: 'Acodia | Grow. Defend. Be Found.',
    description: 'Specialist London practice: Acodia Watch security monitoring, expert-led career training, and digital growth strategy for ambitious businesses.',
    path: '/',
    ogImage: `${SITE_URL}/images/og/og-homepage.png`,
    jsonLd: [ORGANIZATION_SCHEMA, LOCAL_BUSINESS_SCHEMA, FAQ_SCHEMA],
  });

  return (
    <div data-testid="homepage" className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <Navbar />
      <main>
        {/* 1. Hero */}
        <HeroSection />
        {/* 2. Marquee */}
        <MarqueeSection />
        {/* 3. Trust Bar */}
        <TrustBar />
        {/* 4. (Stats in hero only — no duplicate band) */}
        {/* 5. Free Security Checks — Brand Impersonation Watch + Domain teaser (replaced coming-soon waitlist 2026-06-05) */}
        <FreeSecurityChecks />
        {/* 5a. Services — Four Areas of Expertise (detailed cards) */}
        <ServicesSection />
        {/* 5b. Acodia Watch pricing */}
        <AcodiaWatchSection />
        {/* 7. Terminal Section */}
        <TerminalSection />
        {/* 8. Courses */}
        <CoursesSection />
        {/* 10. Why Acodia */}
        <WhySection />
        {/* 11. Process */}
        <ProcessSection />
        {/* 12. CTA Band */}
        <CTABand />
        {/* 13. FAQ */}
        <FAQSection />
        {/* 14. Contact */}
        <ContactSection />
        {/* 15. Footer */}
        <Footer />
      </main>
    </div>
  );
}
